import { get, set, del, keys, createStore } from 'idb-keyval';
import { logWarn } from './logger';

/**
 * File d'attente de mutations à rejouer quand l'app retrouve du réseau.
 *
 * Stockage : IndexedDB via idb-keyval (léger, pas de SQL).
 *  - Une clé = UUID `client_request_id`
 *  - Une valeur = `QueuedMutation` (typed strict)
 *
 * Flow :
 *   1. Composant veut muter une opp/note → appelle `enqueue(mutation)`
 *   2. Optimistic UI : le store Zustand applique le changement local immédiatement
 *   3. Si online → `flush()` est appelé après enqueue, replay direct
 *   4. Si offline → la mutation reste en queue ; au prochain `online` event,
 *      ou au sync background, on `flush()` automatiquement
 *
 * Idempotency : `client_request_id` envoyé dans la mutation Supabase. Côté DB
 * la contrainte UNIQUE WHERE NOT NULL empêche les doublons.
 *
 * Pas de tentative de gérer les conflits ici — c'est un problème métier,
 * géré dans les stores au cas par cas (cf. ENGINEERING.md "Conflits offline").
 *
 * Monitoring quota : `checkStorageQuota()` retourne le ratio used/quota.
 * On log un warning si > 80%, on prune les mutations anciennes (> 30j) en cas
 * de quota saturé.
 */

const STORE_NAME = 'aq-offline-queue';
const offlineStore = createStore('aq-offline-db', STORE_NAME);

export type MutationOp = 'insert' | 'update' | 'delete';

export interface QueuedMutation {
  /** UUID v4, sert aussi de client_request_id en DB */
  id: string;
  /** Nom de la table Supabase ciblée */
  table: string;
  /** Opération */
  op: MutationOp;
  /** Payload : ligne complète pour insert/update, { id } pour delete */
  payload: Record<string, unknown>;
  /** Filtre `eq` pour update / delete (ex: { id: 'abc' }) */
  match?: Record<string, unknown>;
  /** Timestamp ISO de mise en file */
  enqueued_at: string;
  /** Nb de tentatives échouées (pour backoff) */
  attempts: number;
}

export async function enqueue(mut: Omit<QueuedMutation, 'enqueued_at' | 'attempts'>): Promise<void> {
  const full: QueuedMutation = {
    ...mut,
    enqueued_at: new Date().toISOString(),
    attempts: 0,
  };
  await set(mut.id, full, offlineStore);
}

export async function dequeue(id: string): Promise<void> {
  await del(id, offlineStore);
}

export async function listPending(): Promise<QueuedMutation[]> {
  const allKeys = await keys(offlineStore);
  const items: QueuedMutation[] = [];
  for (const k of allKeys) {
    const v = await get<QueuedMutation>(String(k), offlineStore);
    if (v) items.push(v);
  }
  return items.sort((a, b) => a.enqueued_at.localeCompare(b.enqueued_at));
}

export async function markAttempted(id: string): Promise<void> {
  const item = await get<QueuedMutation>(id, offlineStore);
  if (!item) return;
  await set(id, { ...item, attempts: item.attempts + 1 }, offlineStore);
}

export async function size(): Promise<number> {
  const allKeys = await keys(offlineStore);
  return allKeys.length;
}

export function generateClientRequestId(): string {
  // crypto.randomUUID dispo en navigateurs modernes + Node ≥ 16
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback : timestamp + random (suffisant car on hash côté DB)
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Retourne l'utilisation IndexedDB / Cache Storage en bytes.
 * `null` si l'API n'est pas supportée (Safari < 15 sur certaines versions).
 */
export interface QuotaInfo {
  usage: number;
  quota: number;
  ratio: number; // 0..1
}

export async function checkStorageQuota(): Promise<QuotaInfo | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  const est = await navigator.storage.estimate();
  const usage = est.usage ?? 0;
  const quota = est.quota ?? 0;
  const ratio = quota > 0 ? usage / quota : 0;
  if (ratio > 0.8) {
    logWarn('offline', 'storage quota > 80%', {
      usageMB: Math.round(usage / 1024 / 1024),
      quotaMB: Math.round(quota / 1024 / 1024),
      ratio: ratio.toFixed(2),
    });
  }
  return { usage, quota, ratio };
}

/**
 * Supprime les mutations vieilles de plus de `maxAgeDays` (default 30).
 * À appeler quand on détecte un quota saturé.
 */
export async function pruneOldMutations(maxAgeDays = 30): Promise<number> {
  const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
  const pending = await listPending();
  let removed = 0;
  for (const item of pending) {
    if (new Date(item.enqueued_at).getTime() < cutoff) {
      await dequeue(item.id);
      removed++;
    }
  }
  if (removed > 0) {
    logWarn('offline', `pruned ${removed} stale mutations`, { maxAgeDays });
  }
  return removed;
}
